import { useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Menu, ChevronDown, User, LogOut, School as SchoolIcon } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import type { Role } from '@/types/auth'
import { SearchBox } from '@/app/SearchBox'

interface Props {
  onMenuClick: () => void
  showBrand: boolean // mobile: show the brand next to the menu button
  onLogout: () => void
}

function roleLabel(role: Role): string {
  return role === 'ADMIN'
    ? 'Platform Admin'
    : role === 'SCHOOL_ADMIN'
      ? 'School Admin'
      : role === 'PRINCIPAL'
        ? 'Principal'
        : 'Teacher'
}

/** 64px top bar: menu/collapse toggle, brand on mobile, user avatar dropdown. */
export function TopBar({ onMenuClick, showBrand, onLogout }: Props) {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()

  return (
    <header className="flex h-16 flex-none items-center border-b border-slate-200/60 bg-white px-2">
      <button
        onClick={onMenuClick}
        aria-label="Toggle navigation"
        className="rounded-sm p-2.5 text-slate-600 transition-colors hover:bg-slate-100"
      >
        <Menu size={22} />
      </button>

      {showBrand && (
        <div className="ml-2 flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary">
            <SchoolIcon size={18} className="text-white" />
          </div>
          <span className="text-headline-md font-bold tracking-tight text-slate-900">Novitiq</span>
        </div>
      )}

X
      <div className="ml-3 hidden min-w-0 flex-1 sm:block">
        <SearchBox />
      </div>

      <div className="ml-auto px-2">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="flex items-center gap-2.5 rounded-md p-1 outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-gradient-primary text-[15px] font-bold text-white shadow-[0_2px_8px_rgba(99,102,241,0.25)]">
              {user?.full_name.charAt(0).toUpperCase() ?? 'U'}
            </div>
            {user && (
              <div className="hidden text-left sm:block">
                <p className="text-title-sm font-semibold text-slate-800">{user.full_name}</p>
                <p className="text-label-sm text-slate-500">{roleLabel(user.role)}</p>
              </div>
            )}
            <ChevronDown size={18} className="text-slate-400" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 min-w-[200px] rounded-md border border-slate-200 bg-white p-1.5 shadow-pop"
            >
              <DropdownMenu.Item
                onSelect={() => navigate('/profile')}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-title-sm font-medium text-slate-700 outline-none data-[highlighted]:bg-primary-soft"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-soft text-primary">
                  <User size={15} />
                </span>
                Profile
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-slate-100" />
              <DropdownMenu.Item
                onSelect={onLogout}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-title-sm font-medium text-danger outline-none data-[highlighted]:bg-danger-soft"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-danger-soft text-danger">
                  <LogOut size={15} />
                </span>
                Sign Out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  )
}
